import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import { useCallback, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import {
  confirmPublicPicking,
  getPublicPickingConfirmation,
} from '../../../../api/mix-deliveriesApi';

export default function PublicPickingCompletePage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState(null);

  const [error, setError] = useState('');

  const [completed, setCompleted] = useState(null);

  const loadConfirmation = useCallback(async () => {
    if (!token) {
      setError('Nieprawidłowy kod QR.');

      setLoading(false);

      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await getPublicPickingConfirmation(token);

      setData(result);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadConfirmation();
  }, [loadConfirmation]);

  const handleConfirm = async () => {
    if (!token || submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await confirmPublicPicking(token);

      setCompleted(result);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSubmitting(false);
    }
  };
  console.log(token);
  // ---------------------------------------------------------------
  // Ładowanie
  // ---------------------------------------------------------------

  if (loading) {
    return (
      <PageContainer>
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
          }}
        >
          <CircularProgress />

          <Typography
            sx={{
              mt: 2,
            }}
            color="text.secondary"
          >
            Sprawdzanie listy pobrań...
          </Typography>
        </Paper>
      </PageContainer>
    );
  }

  // ---------------------------------------------------------------
  // POST wykonany poprawnie
  // ---------------------------------------------------------------

  if (completed) {
    const alreadyCompleted = completed.reason === 'ALREADY_COMPLETED';

    return (
      <PageContainer>
        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 3,
              sm: 4,
            },
            textAlign: 'center',
          }}
        >
          <CheckCircleOutlineIcon
            color="success"
            sx={{
              fontSize: 72,
              mb: 2,
            }}
          />

          <Typography variant="h5" fontWeight={800}>
            {alreadyCompleted
              ? 'Dostawa została już przygotowana'
              : 'Przygotowanie potwierdzone'}
          </Typography>

          <Typography
            color="text.secondary"
            sx={{
              mt: 1,
            }}
          >
            Dostawa
          </Typography>

          <Typography
            variant="h4"
            fontWeight={900}
            sx={{
              mt: 0.5,
            }}
          >
            {completed.delivery?.deliveryNumber}
          </Typography>

          <Chip
            color="success"
            label="PREPARED"
            sx={{
              mt: 3,
              fontWeight: 800,
            }}
          />

          {completed.usedAt && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 3,
              }}
            >
              Potwierdzono: {formatDateTime(completed.usedAt)}
            </Typography>
          )}
        </Paper>
      </PageContainer>
    );
  }

  // ---------------------------------------------------------------
  // Błąd komunikacji
  // ---------------------------------------------------------------

  if (error && !data) {
    return (
      <PageContainer>
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
          }}
        >
          <ErrorOutlineIcon
            color="error"
            sx={{
              fontSize: 64,
              mb: 2,
            }}
          />

          <Typography variant="h6" fontWeight={700}>
            Nie można otworzyć listy pobrań
          </Typography>

          <Alert
            severity="error"
            sx={{
              mt: 3,
              textAlign: 'left',
            }}
          >
            {error}
          </Alert>
        </Paper>
      </PageContainer>
    );
  }

  // ---------------------------------------------------------------
  // Token nieważny
  // ---------------------------------------------------------------

  if (data && !data.valid) {
    return <InvalidTokenPage data={data} />;
  }

  const delivery = data?.delivery;

  // ---------------------------------------------------------------
  // VALID
  // ---------------------------------------------------------------

  return (
    <PageContainer>
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: {
              xs: 2.5,
              sm: 3,
            },

            bgcolor: 'background.default',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <QrCode2Icon
              color="primary"
              sx={{
                fontSize: 36,
              }}
            />

            <Box>
              <Typography variant="h5" fontWeight={800}>
                Potwierdzenie realizacji
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Lista pobrań miksu
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        <Stack
          spacing={3}
          sx={{
            p: {
              xs: 2.5,
              sm: 3,
            },
          }}
        >
          <InfoRow
            label="Klient"
            value={delivery?.recipientName || '—'}
            large
          />

          {delivery?.recipientNumber && (
            <InfoRow label="Numer odbiorcy" value={delivery.recipientNumber} />
          )}

          <Divider />

          <InfoRow
            label="Dostawa"
            value={delivery?.deliveryNumber || '—'}
            large
          />

          <InfoRow label="Transport" value={delivery?.transportNumber || '—'} />

          <InfoRow
            label="Awizacja"
            value={
              delivery?.loadingAppointmentAt
                ? formatDateTime(delivery.loadingAppointmentAt)
                : '—'
            }
          />

          <Box>
            <Typography variant="caption" color="text.secondary">
              Kanapkowanie
            </Typography>

            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={delivery?.sandwichingRequired ? 'TAK' : 'NIE'}
                color={delivery?.sandwichingRequired ? 'warning' : 'default'}
                variant={delivery?.sandwichingRequired ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 800,
                }}
              />
            </Box>
          </Box>

          <Divider />

          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info">
            Potwierdź dopiero po zakończeniu przygotowania całej dostawy.
          </Alert>

          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting}
            onClick={handleConfirm}
            startIcon={
              submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleOutlineIcon />
              )
            }
            sx={{
              minHeight: 56,

              fontSize: 16,
              fontWeight: 800,
            }}
          >
            {submitting ? 'Potwierdzanie...' : 'POTWIERDŹ REALIZACJĘ'}
          </Button>
        </Stack>
      </Paper>
    </PageContainer>
  );
}

// -------------------------------------------------------------------
// Nieważny / wykorzystany token
// -------------------------------------------------------------------

function InvalidTokenPage({ data }) {
  const presentation = getInvalidTokenPresentation(data);

  return (
    <PageContainer>
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 3,
            sm: 4,
          },

          textAlign: 'center',
        }}
      >
        {presentation.success ? (
          <CheckCircleOutlineIcon
            color="success"
            sx={{
              fontSize: 72,
              mb: 2,
            }}
          />
        ) : (
          <ErrorOutlineIcon
            color="error"
            sx={{
              fontSize: 64,
              mb: 2,
            }}
          />
        )}

        <Typography variant="h5" fontWeight={800}>
          {presentation.title}
        </Typography>

        <Typography
          color="text.secondary"
          sx={{
            mt: 1,
          }}
        >
          {presentation.message}
        </Typography>

        {data?.delivery?.deliveryNumber && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Dostawa
            </Typography>

            <Typography variant="h5" fontWeight={800}>
              {data.delivery.deliveryNumber}
            </Typography>
          </Box>
        )}

        {data?.usedAt && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 3,
            }}
          >
            Potwierdzono: {formatDateTime(data.usedAt)}
          </Typography>
        )}
      </Paper>
    </PageContainer>
  );
}

// -------------------------------------------------------------------
// Layout
// -------------------------------------------------------------------

function PageContainer({ children }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',

        bgcolor: 'grey.100',

        display: 'flex',

        alignItems: {
          xs: 'flex-start',
          sm: 'center',
        },

        py: {
          xs: 2,
          sm: 4,
        },
      }}
    >
      <Container maxWidth="sm">{children}</Container>
    </Box>
  );
}

function InfoRow({ label, value, large = false }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Typography
        variant={large ? 'h6' : 'body1'}
        fontWeight={large ? 800 : 700}
        sx={{
          mt: 0.25,

          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// -------------------------------------------------------------------
// Statusy tokenu
// -------------------------------------------------------------------

function getInvalidTokenPresentation(data) {
  switch (data?.reason) {
    case 'USED':
      return {
        success: true,

        title: 'Dostawa została już przygotowana',

        message: 'Ten kod QR został już wykorzystany.',
      };

    case 'REVOKED':
      return {
        success: false,

        title: 'Nieaktualna lista pobrań',

        message:
          'Ten kod QR został unieważniony. Użyj najnowszej wydrukowanej listy pobrań.',
      };

    case 'EXPIRED':
      return {
        success: false,

        title: 'Kod QR stracił ważność',

        message: 'Ta lista pobrań nie może już zostać użyta do potwierdzenia.',
      };

    case 'DELIVERY_NOT_ACTIVE':
      return {
        success: false,

        title: 'Dostawa nie jest aktywna',

        message: 'Nie można potwierdzić przygotowania tej dostawy.',
      };

    case 'INVALID_STATUS':
      return {
        success: false,

        title: 'Nie można potwierdzić dostawy',

        message: 'Dostawa nie znajduje się już w statusie IN_PROGRESS.',
      };

    case 'NOT_FOUND':
    default:
      return {
        success: false,

        title: 'Nieprawidłowy kod QR',

        message: 'Nie znaleziono listy pobrań odpowiadającej temu kodowi.',
      };
  }
}

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',

    day: '2-digit',
    month: '2-digit',
    year: 'numeric',

    hour: '2-digit',
    minute: '2-digit',

    hourCycle: 'h23',
  }).format(date);
}

function getErrorMessage(error) {
  const message = error?.response?.data?.message;

  if (Array.isArray(message)) {
    return message.join('\n');
  }

  return (
    message || error?.message || 'Wystąpił błąd podczas komunikacji z serwerem.'
  );
}
